import styled from '@emotion/styled'
import { css } from '@emotion/react'
import { Box } from '@mui/material'

export const Container = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding: 16px;
`

export const AlertBox = styled(Box)`
  ${() => css`
    display: flex;
    gap: 12px;
    padding: 12px 16px;
    background: #FFF4E5;
    border: 1px solid #FFE0B2;
    border-radius: 8px;
    align-items: flex-start;
  `}
`

export const AlertIcon = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  
  svg {
    display: block;
    color: #f59e0b;
  }
`

export const AlertText = styled.span`
  ${({ theme }) => css`
    font-size: 13px;
    line-height: 1.5;
    color: ${theme.colors.gray.dark};
  `}
`

export const CollaboratorHeader = styled(Box)`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
`

export const CollaboratorInfo = styled(Box)`
  display: flex;
  align-items: center;
  gap: 12px;
`

export const Avatar = styled(Box)`
  ${({ theme }) => css`
    width: 48px;
    height: 48px;
    border-radius: 50%;
    background: ${theme.colors.primary};
    color: ${theme.colors.lightest};
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 16px;
    font-weight: 600;
    flex-shrink: 0;
  `}
`

export const CollaboratorDetails = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: 4px;
`

export const CollaboratorName = styled(Box)`
  ${({ theme }) => css`
    font-size: 16px;
    font-weight: 600;
    color: ${theme.colors.gray.dark};
    line-height: 1.2;
  `}
`

export const CollaboratorRegistration = styled(Box)`
  ${({ theme }) => css`
    font-size: 12px;
    font-weight: 400;
    color: ${theme.colors.gray[400]};
    line-height: 1.2;
  `}
`

export const StatusBadge = styled(Box)<{ color: string }>`
  ${({ color }) => css`
    display: inline-flex;
    align-items: center;
    padding: 6px 16px;
    border-radius: 16px;
    background: ${color}20;
    color: ${color};
    font-size: 13px;
    font-weight: 500;
    white-space: nowrap;
  `}
`

export const FieldsRow = styled(Box)`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`

export const Section = styled(Box)`
  ${({ theme }) => css`
    border: 1px solid ${theme.colors.border.light};
    border-radius: 8px;
    overflow: hidden;
  `}
`

export const SectionHeader = styled(Box)`
  ${() => css`
    display: flex;
    align-items: center;
    gap: 16px;
    padding: 16px;
    background: #FAFAFA;
    cursor: pointer;
    transition: background 0.2s;

    &:hover {
      background: #F5F5F5;
    }
  `}
`

export const SectionTitleContainer = styled(Box)`
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 12px;
`

/** Espelha ItemRight: total alinhado aos valores das linhas + slot de 32px. */
export const SectionHeaderRight = styled(Box)`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 12px;
  min-width: 0;
`

export const SectionIcon = styled.span<{ color?: string; background?: string }>`
  ${({ color, background }) => css`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    border-radius: 8px;
    background: ${background || '#E3F2FD'};
    color: ${color || '#2B6CB0'};
    flex-shrink: 0;
    
    & > * {
      display: flex;
      align-items: center;
      justify-content: center;
    }
    
    svg {
      display: block;
    }
  `}
`

export const SectionTitle = styled.span`
  ${({ theme }) => css`
    font-size: 16px;
    font-weight: 600;
    color: ${theme.colors.gray.dark};
  `}
`

export const SectionTotal = styled.span`
  ${({ theme }) => css`
    font-size: 16px;
    font-weight: 600;
    color: ${theme.colors.gray.dark};
  `}
`

export const SectionContent = styled(Box)`
  ${({ theme }) => css`
    border-top: 1px solid ${theme.colors.border.light};
  `}
`

export const Item = styled(Box)`
  ${({ theme }) => css`
    display: flex;
    align-items: center;
    gap: 16px;
    padding: 12px 16px;
    border-bottom: 1px solid ${theme.colors.border.light};

    &:last-child {
      border-bottom: none;
    }
  `}
`

export const ItemLeft = styled(Box)`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
`

export const ItemName = styled.span`
  ${({ theme }) => css`
    font-size: 14px;
    font-weight: 400;
    color: ${theme.colors.gray.dark};
  `}
`

export const ItemCode = styled.span`
  ${({ theme }) => css`
    font-size: 12px;
    font-weight: 400;
    color: ${theme.colors.gray[400]};
  `}
`

export const ItemRight = styled(Box)`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 12px;
  min-width: 0;
`

/** Reserva o mesmo espaço do lápis nas linhas sem edição para alinhar valores à direita. */
export const ItemActionSlot = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 32px;
  height: 32px;
`

export const ItemValue = styled.span<{ isNegative?: boolean }>`
  ${({ theme, isNegative }) => css`
    font-size: 14px;
    font-weight: 500;
    color: ${isNegative ? '#E00' : theme.colors.gray.dark};
    white-space: nowrap;
  `}
`

export const EditButton = styled.button`
  ${({ theme }) => css`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    border: none;
    background: transparent;
    color: ${theme.colors.gray[400]};
    cursor: pointer;
    border-radius: 4px;
    transition: all 0.2s;

    &:hover {
      background: ${theme.colors.gray[200]};
      color: ${theme.colors.primary};
    }
  `}
`

export const Footer = styled(Box)`
  display: flex;
  justify-content: flex-end;
  padding-top: 16px;
  border-top: 1px solid ${({ theme }) => theme.colors.border.light};
`
